import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { DenialCode } from '../auth/denial';
import { isDenialCode, loginRequestFor } from '../auth/denial';
import { msalInstance } from '../auth/msalConfig';

interface LoginSearch {
  /** Why the API refused an otherwise valid session, when it did. */
  denied?: DenialCode;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch =>
    isDenialCode(search.denied) ? { denied: search.denied } : {},
  beforeLoad: ({ search }) => {
    // A refused account still has a live MSAL session, on purpose — there is
    // nothing wrong with it to clear. Without this guard that session would
    // bounce straight back to `/`, be refused again, and return here: the loop
    // would run on its own rather than needing a click.
    if (search.denied) {
      return;
    }
    if (msalInstance.getActiveAccount()) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginRoute,
});

function LoginRoute() {
  const { t } = useTranslation();
  const { denied } = Route.useSearch();

  function handleLogin() {
    void msalInstance.loginRedirect(loginRequestFor(denied));
  }

  /** The only useful action when refused: come back as somebody else. */
  function handleSignOut() {
    void msalInstance.logoutRedirect();
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink text-(--inverse-fg)">
      {/* Electric-blue tilted slab — Epitech-style hero accent. Small text that
          can sit over it stays at /85 or above: on this blue, /65 measures
          3.4:1 and /60 3.1:1, under the 4.5:1 that axe requires. */}
      <div
        className="pointer-events-none absolute top-0 right-0 h-full w-[46%] origin-top-right transform-[skewX(-8deg)_translateX(20%)] bg-blue"
        aria-hidden
      />

      <header className="relative z-10 mx-auto flex h-20 max-w-[1280px] items-center justify-between px-10">
        <span className="bracket-wrap on-dark display text-2xl">
          TRONCHE2TEK
        </span>
        <span className="label text-inverse-fg/85">{t('login.topMeta')}</span>
      </header>

      <main className="relative z-10 mx-auto grid max-w-[1280px] items-end gap-16 px-10 pt-20 pb-32 lg:grid-cols-[1.4fr_1fr]">
        <section>
          <p className="label dash on-dark mb-8 text-inverse-fg/80">
            {t('login.kicker')}
          </p>

          <h1 className="display mb-4 text-[120px]">
            {t('login.headlineLine1')}
            <br />
            {t('login.headlineLine2')}
            <span className="cursor on-dark"></span>
          </h1>
          <p className="display text-[44px] text-inverse-fg/35">
            {t('login.subhead')}
          </p>

          <p className="my-12 max-w-md text-base/relaxed text-inverse-fg/75">
            {t('login.body')}
          </p>

          {denied && (
            <div
              role="alert"
              className="mb-12 max-w-md border-l-3 border-l-magenta bg-inverse-fg/8 px-5 py-4"
            >
              <p className="label mb-2 text-magenta">
                {t(`login.denied.${denied}.title`)}
              </p>
              <p className="text-sm/relaxed text-inverse-fg/85">
                {t(`login.denied.${denied}.body`)}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <button onClick={handleLogin} className="btn-primary">
              {t('login.button')}
              <span>→</span>
            </button>
            {denied ? (
              <button onClick={handleSignOut} className="btn-link on-dark">
                {t('login.denied.signOut')}
              </button>
            ) : (
              <span className="label text-inverse-fg/85">
                {t('login.security')}
              </span>
            )}
          </div>
        </section>

        <aside className="hidden border-l-2 border-inverse-fg/30 pl-8 lg:block">
          <p className="label mb-10 text-inverse-fg/85">
            {t('login.featuresLabel')}
          </p>
          <ul className="space-y-7">
            <Feature
              num="01"
              title={t('login.features.annuaire.title')}
              body={t('login.features.annuaire.body')}
            />
            <Feature
              num="02"
              title={t('login.features.cards.title')}
              body={t('login.features.cards.body')}
            />
            <Feature
              num="03"
              title={t('login.features.moderation.title')}
              body={t('login.features.moderation.body')}
            />
          </ul>
        </aside>
      </main>

      <footer className="relative z-10 border-t border-inverse-fg/20">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-10 py-4">
          <p className="label dash on-dark text-inverse-fg/65">
            {t('login.footerLabel')}
          </p>
          <p className="label text-inverse-fg/85">{t('login.footerMeta')}</p>
        </div>
      </footer>
    </div>
  );
}

function Feature({
  num,
  title,
  body,
}: {
  num: string;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-5">
      <span className="display mt-1 shrink-0 text-base text-green">{num}</span>
      <div>
        <p className="display mb-2 text-base">{title}</p>
        <p className="text-sm/relaxed text-inverse-fg/85">{body}</p>
      </div>
    </li>
  );
}
